import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SectionHeader, EmptyState, FingerprintChip } from "../components/UI";
import Avatar from "../components/Avatar";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import { Camera, Save, KeyRound, ScrollText, Download, FileCheck2 } from "lucide-react";

export default function Profile() {
  const { user, refreshMe } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "", department: user?.department || "", title: user?.title || "",
    phone: user?.phone || "", bio: user?.bio || "", location: user?.location || "",
  });
  const [saving, setSaving] = useState(false);
  const [passwords, setPasswords] = useState({ current: "", next: "" });
  const [changingPassword, setChangingPassword] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [acks, setAcks] = useState([]);
  const [loadingAcks, setLoadingAcks] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const avatarRef = useRef(null);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "", department: user.department || "", title: user.title || "",
        phone: user.phone || "", bio: user.bio || "", location: user.location || "",
      });
    }
  }, [user]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoadingAcks(true);
      try {
        const { data } = await api.get("/my/policy-acknowledgments");
        if (active) setAcks(data || []);
      } catch { /* ignore */ }
      finally { if (active) setLoadingAcks(false); }
    })();
    return () => { active = false; };
  }, []);

  const downloadAckPdf = async (ack) => {
    setDownloadingId(ack.id);
    try {
      const res = await api.get(`/my/policy-acknowledgments/${ack.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (ack.policy_name || "policy").replace(/[^\w-]+/g, "_");
      a.download = `PropHR-Signed-${safeName}-${ack.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Downloaded signed policy");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    } finally {
      setDownloadingId(null);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/users/me", form);
      await refreshMe();
      toast.success("Profile updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshMe();
      toast.success("Avatar updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setChangingPassword(true);
    try {
      await api.post("/users/me/password", { current_password: passwords.current, new_password: passwords.next });
      toast.success("Password changed");
      setPasswords({ current: "", next: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setChangingPassword(false); }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <SectionHeader title="My Profile" subtitle="Personal information used across PropHR." />

      <div className="data-cell p-6 mb-6 flex items-center gap-6">
        <div className="relative">
          <Avatar user={user} size={80}/>
          <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadAvatar(e.target.files?.[0])} data-testid={TID.profileAvatarInput}/>
          <button
            onClick={() => avatarRef.current?.click()}
            disabled={uploading}
            className="absolute -bottom-1 -right-1 w-8 h-8 bg-white border border-slate-300 rounded-full flex items-center justify-center hover:border-blue-600 transition-colors"
            data-testid="profile-avatar-upload-btn"
            title="Upload profile photo"
          >
            <Camera size={14} strokeWidth={2} className="text-slate-700"/>
          </button>
        </div>
        <div className="flex-1">
          <div className="font-display text-xl font-semibold text-slate-900">{user?.name}</div>
          <div className="text-sm text-slate-500">{user?.email}</div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <span className="pill pill-closed">{user?.role?.replaceAll("_"," ")}</span>
            {user?.department && <span className="pill pill-closed">{user.department}</span>}
            {user?.title && <span className="pill pill-closed">{user.title}</span>}
          </div>
        </div>
      </div>

      <form onSubmit={save} className="data-cell p-6 mb-6">
        <div className="eyebrow mb-4">Personal Details</div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Full name" value={form.name} onChange={v => setForm({...form, name: v})} testid={TID.profileName} required/>
          <Field label="Job title" value={form.title} onChange={v => setForm({...form, title: v})}/>
          <Field label="Department" value={form.department} onChange={v => setForm({...form, department: v})}/>
          <Field label="Phone" value={form.phone} onChange={v => setForm({...form, phone: v})}/>
          <Field label="Location" value={form.location} onChange={v => setForm({...form, location: v})} className="md:col-span-2"/>
          <div className="md:col-span-2">
            <label className="eyebrow block mb-1">Bio</label>
            <textarea rows={3} value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} className="input"/>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <button className="btn-primary" disabled={saving} data-testid={TID.profileSave}>
            <Save size={14} strokeWidth={2}/> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>

      <form onSubmit={changePassword} className="data-cell p-6">
        <div className="eyebrow mb-4 flex items-center gap-2"><KeyRound size={12} strokeWidth={2}/> Change Password</div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Current password" type="password" value={passwords.current} onChange={v => setPasswords({...passwords, current: v})} testid={TID.passwordCurrent} required/>
          <Field label="New password (min 8 chars)" type="password" value={passwords.next} onChange={v => setPasswords({...passwords, next: v})} testid={TID.passwordNew} required/>
        </div>
        <div className="mt-5 flex justify-end">
          <button className="btn-primary" disabled={changingPassword} data-testid={TID.passwordSubmit}>
            <KeyRound size={14} strokeWidth={2}/> {changingPassword ? "Updating…" : "Update password"}
          </button>
        </div>
      </form>

      {/* Signed Policies (downloadable PDFs) */}
      <section className="mt-8" data-testid="my-signed-policies">
        <SectionHeader
          title="My signed policies"
          subtitle="Every policy you've acknowledged is saved here as a tamper-evident PDF, fingerprinted with SHA-256 and stored under your organization's retention policy."
          right={
            <div className="flex items-center gap-1.5 text-[10px] font-mono text-slate-400 uppercase tracking-widest">
              <FileCheck2 size={12} strokeWidth={1.75} />
              <span>{acks.length} record{acks.length === 1 ? "" : "s"}</span>
            </div>
          }
        />
        {loadingAcks ? (
          <div className="border border-slate-200 p-8 text-center text-sm text-slate-500 font-mono bg-slate-50/40">
            Loading signed policies…
          </div>
        ) : acks.length === 0 ? (
          <EmptyState
            title="Nothing signed yet"
            subtitle="Once you sign a policy from the Policies tab, a downloadable PDF will show up here."
            icon={ScrollText}
          />
        ) : (
          <div className="border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm" data-testid="signed-policies-table">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-widest text-slate-400 bg-slate-50">
                  <th className="text-left px-4 py-2.5 font-mono font-normal">Policy</th>
                  <th className="text-left px-4 py-2.5 font-mono font-normal hidden sm:table-cell">Version</th>
                  <th className="text-left px-4 py-2.5 font-mono font-normal hidden md:table-cell">Signed on</th>
                  <th className="text-left px-4 py-2.5 font-mono font-normal hidden lg:table-cell">Fingerprint</th>
                  <th className="text-right px-4 py-2.5 font-mono font-normal">Actions</th>
                </tr>
              </thead>
              <tbody>
                {acks.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
                          <FileCheck2 size={14} strokeWidth={1.75} className="text-emerald-700" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate max-w-xs">{a.policy_name}</div>
                          <div className="text-[11px] text-slate-500 font-mono">
                            Signed as {a.signed_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="pill pill-closed">v{a.policy_version}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-slate-600 text-xs font-mono">
                      {new Date(a.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" })}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <FingerprintChip fingerprint={a.acknowledgment_fingerprint} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => downloadAckPdf(a)}
                        disabled={downloadingId === a.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 hover:border-slate-900 text-xs text-slate-700 hover:text-slate-900 transition-colors disabled:opacity-50 rounded-sm"
                        data-testid={`download-ack-${a.id}`}
                      >
                        <Download size={12} strokeWidth={1.75} />
                        {downloadingId === a.id ? "Preparing…" : "PDF"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, className = "" }) {
  return (
    <div className={className}>
      <label className="eyebrow block mb-1">{label}</label>
      <input
        required={required}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="input"
      />
    </div>
  );
}
